import { put, takeLeading, call, select } from 'redux-saga/effects';

import { getStripe } from '../../../../../util/api/StripeApi';
import {
  getStripeRequest,
  getStripeResponse,
} from '../../../reducers/ducks/legacy/StripeDuck';
import * as orderActions from '../../../reducers/ducks/legacy/OrderDuck';
import notifyBugsnag from '../../../../../util/notifyBugsnag';
import parseStaleCheckoutError from '../../../../../util/parseStaleCheckoutError';
import {
  canAttemptStaleRecovery,
  markStaleRecoveryAttempt,
  notifyStaleRecovered,
  notifyStaleRetryExhausted,
} from '../../../../../util/staleCheckoutRecovery';
import Notification from '../../../../../components/layout/Notification';
import { checkoutStart } from './OrderSaga';
import orderTimingGuard from './orderTimingGuard';
import { getOrderTiming } from '../../../../../util/orderTiming';

const rewriteSuccessUrl = (
  originalUrl,
  oldId,
  newId,
  oldRestaurant,
  newRestaurant
) => {
  if (!originalUrl || !newId) return originalUrl;
  let next = originalUrl;
  if (oldId && next.includes(oldId)) {
    next = next.split(oldId).join(newId);
  }
  if (oldRestaurant && newRestaurant && next.includes(oldRestaurant)) {
    next = next.split(oldRestaurant).join(newRestaurant);
  }
  return next;
};

export function* getStripeSaga({ payload }) {
  const { showError, suppressErrorReport, ...stripePayload } = payload;
  const timing = yield select((state) => getOrderTiming(state));
  try {
    if (!(yield call(orderTimingGuard, true))) return;
    const checkoutId = yield select((state) => state.order.checkoutData?.id);
    if (stripePayload.checkout !== checkoutId) return;
    const response = yield call(getStripe, stripePayload);
    const current = yield select((state) => getOrderTiming(state));
    const currentCheckoutId = yield select(
      (state) => state.order.checkoutData?.id
    );
    if (
      current.key !== timing.key ||
      currentCheckoutId !== checkoutId ||
      !(yield call(orderTimingGuard, true))
    )
      return;
    yield put(getStripeResponse({ response }));
  } catch (error) {
    const staleInfo = parseStaleCheckoutError(error);
    const staleCheckoutId = staleInfo ? stripePayload.checkout ?? null : null;
    let staleHandled = false;

    if (staleInfo) {
      if (canAttemptStaleRecovery(staleCheckoutId)) {
        markStaleRecoveryAttempt(staleCheckoutId);
        Notification.info('Refreshing your checkout\u2026', {
          toastId: 'CheckoutStaleRetry',
        });
        yield put(orderActions.setCheckoutData(null));
        try {
          yield call(checkoutStart);
          const freshCheckoutData = yield select(
            ({ order }) => order.checkoutData
          );
          if (freshCheckoutData?.id) {
            if (!(yield call(orderTimingGuard, true, timing.key))) return;
            const retryPayload = {
              ...stripePayload,
              checkout: freshCheckoutData.id,
              client: freshCheckoutData.restaurant || stripePayload.client,
              success_url: rewriteSuccessUrl(
                stripePayload.success_url,
                staleCheckoutId,
                freshCheckoutData.id,
                stripePayload.client,
                freshCheckoutData.restaurant
              ),
            };
            const retried = yield call(getStripe, retryPayload);
            const retryCurrentId = yield select(
              (state) => state.order.checkoutData?.id
            );
            if (
              retryCurrentId !== freshCheckoutData.id ||
              !(yield call(orderTimingGuard, true, timing.key))
            )
              return;
            yield put(getStripeResponse({ response: retried }));
            if (!suppressErrorReport) {
              notifyStaleRecovered({
                checkoutId: staleCheckoutId,
                source: 'stripe/redirect',
                status: staleInfo.status,
                errorCode: staleInfo.errorCode,
              });
            }
            return;
          }
        } catch (recoveryErr) {
          if (!suppressErrorReport) {
            notifyBugsnag(recoveryErr);
          }
        }
      } else if (!suppressErrorReport) {
        notifyStaleRetryExhausted({
          checkoutId: staleCheckoutId,
          source: 'stripe/redirect',
          status: staleInfo.status,
          errorCode: staleInfo.errorCode,
        });
      }
      staleHandled = true;
    }

    if (!staleHandled && !suppressErrorReport) {
      notifyBugsnag(error);
    }
    yield put(getStripeResponse({ error }));
    if (typeof showError === 'function') {
      showError(
        error?.data?.message || 'Unable to start payment. Please try again.'
      );
    }
  }
}

export function* watchStripeSagas() {
  yield takeLeading(getStripeRequest.type, getStripeSaga);
}
